import { useCallback, useEffect, useId, useState } from 'react'
import Tabs from '../components/Tabs'
import ThumbIcon from '../components/ThumbIcon'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/format'
import Markdown from '../lib/markdown'

// The result detail view: one call's markdown write-up, plus the on-demand raw
// transcript (the only surface that shows verbatim content). The result payload
// itself stays transcript-free — the transcript loads from its own endpoint when
// someone asks for it.
//
// This is also where a call is rated: thumbs up/down live on the call being read,
// not on the link, so a later call never overwrites the verdict on an earlier one.
//
// Filing a copy is `window.print()`: the print stylesheet in main.css hides the
// chrome and lays this page out for paper. There is no server-rendered export — a
// second renderer would mean the file and the screen could disagree about the
// same write-up, which is the only thing an export has to get right. The
// transcript is `.no-print` and stays out of it.

export default function ResultPage({
  spaceId,
  linkId,
  initialAttempt,
  onBack,
  onAttemptChange,
  backLabel = 'Back to the space',
}) {
  const tabsId = useId()
  // Every link route lives under its space (admin-only here: the result, the
  // rating and the transcript are never a principal's).
  const base = `/api/v1/spaces/${spaceId}/links/${linkId}`
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(initialAttempt ?? null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    // `attempt` tells the server which call was opened, so it stamps the right
    // call's reviewed_at. Absent = the latest.
    const query = initialAttempt ? `?attempt=${initialAttempt}` : ''
    api(`${base}/result${query}`)
      .then((payload) => {
        setData(payload)
        const asked = payload.calls.find((c) => c.attempt === initialAttempt)
        const latest = payload.calls[payload.calls.length - 1]
        setSelected((asked ?? latest)?.attempt ?? null)
      })
      .catch((err) => setError(err.detail || 'Could not load this result.'))
      .finally(() => setLoading(false))
  }, [base, initialAttempt])

  useEffect(load, [load])

  function selectAttempt(attempt) {
    setSelected(attempt)
    // Keep the URL on the call being read, so a copied link opens the same one.
    onAttemptChange?.(attempt)
  }

  // A saved verdict is patched into the loaded payload rather than triggering a
  // reload: the rating buttons and the call tabs both read it, and refetching
  // would throw away the reader's scroll position mid-read.
  function applyRating(attemptNumber, out) {
    setData((current) => ({
      ...current,
      calls: current.calls.map((c) =>
        c.attempt === attemptNumber ? { ...c, rating: out.rating, rated_at: out.rated_at } : c,
      ),
    }))
  }

  const call = data?.calls.find((c) => c.attempt === selected) ?? null

  return (
    <main
      className="w-full px-4 md:w-[85%] md:mx-auto md:px-0"
      style={{ paddingTop: 'var(--space-l)', paddingBottom: 'var(--space-2xl)' }}
    >
      <button
        type="button"
        className="btn btn--ghost no-print"
        onClick={onBack}
        style={{ marginBottom: 'var(--space-s)' }}
      >
        ← {backLabel}
      </button>

      {loading && (
        <div className="card" role="status">
          <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }}>
            Loading result…
          </p>
        </div>
      )}

      {!loading && error && (
        <div className="alert alert--error" role="alert">
          <div className="flex flex-col" style={{ gap: 'var(--space-xs)' }}>
            <p className="text-body-md">{error}</p>
            <div>
              <button type="button" className="btn btn--secondary btn--sm" onClick={load}>
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && data && (
        <div className="flex flex-col" style={{ gap: 'var(--space-s)' }}>
          <div className="card">
            <h2 className="text-h3">
              {data.participant.first_name} {data.participant.last_name}
            </h2>
            <p className="text-body-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {data.participant.email}
            </p>
            <p className="text-body-md" style={{ marginTop: 'var(--space-xs)' }}>
              {data.template_name}
              <span className="chip" style={{ marginLeft: 'var(--space-2xs)' }}>
                {data.status_label}
              </span>
            </p>

            {data.variables.length > 0 && (
              <dl style={{ margin: 'var(--space-s) 0 0' }}>
                {data.variables.map((entry) => (
                  <div className="fact-row" key={entry.label}>
                    <dt>{entry.label}</dt>
                    <dd>{entry.value || '—'}</dd>
                  </div>
                ))}
              </dl>
            )}

            <dl
              className="grid grid-cols-2 md:grid-cols-3"
              style={{ gap: 'var(--space-s)', marginTop: 'var(--space-m)' }}
            >
              <Timestamp label="Sent" value={data.email_sent_at} />
              <Timestamp label="Link opened" value={data.opened_at} />
              <Timestamp label="First call started" value={data.started_at} />
              <Timestamp label="Last call ended" value={data.last_call_at} />
              <Timestamp label="Link expires" value={data.expires_at} />
            </dl>
          </div>

          {data.calls.length === 0 && (
            <div
              className="card"
              style={{
                textAlign: 'center',
                paddingTop: 'var(--space-xl)',
                paddingBottom: 'var(--space-xl)',
              }}
            >
              <h3 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
                No calls yet
              </h3>
              <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }}>
                Calls appear here as soon as one ends.
              </p>
            </div>
          )}

          {data.calls.length > 0 && (
            <>
              {/* Call switcher: navigation, so it prints as nothing. The selected
                  call's own header says which call it is. */}
              <Tabs
                id={tabsId}
                className="no-print"
                label={`${data.call_count} call${data.call_count === 1 ? '' : 's'}`}
                selected={selected}
                onSelect={selectAttempt}
                tabs={data.calls.map((c) => ({
                  id: c.attempt,
                  panelId: `${tabsId}-panel`,
                  label: (
                    <>
                      Call {c.attempt}
                      {/* The verdict rides along on the tab so switching between
                          calls shows which ones have already been judged. */}
                      {c.rating && (
                        <span className={`rating-mark rating-mark--${c.rating}`}>
                          <ThumbIcon
                            direction={c.rating === 'up' ? 'up' : 'down'}
                            size={16}
                            filled
                          />
                        </span>
                      )}
                    </>
                  ),
                }))}
              />

              <div
                role="tabpanel"
                id={`${tabsId}-panel`}
                aria-labelledby={`${tabsId}-tab-${selected}`}
                className="flex flex-col"
                style={{ gap: 'var(--space-s)' }}
              >
                {call && <CallPanel call={call} base={base} onRated={applyRating} />}

                {/* Never printed: the transcript is exactly what must stay inside
                    this page. */}
                {call && call.has_transcript && (
                  <TranscriptCard key={call.attempt} base={base} call={call} />
                )}
              </div>
            </>
          )}

          <p
            className="text-caption no-print"
            style={{ color: 'var(--color-text-tertiary)', textAlign: 'center' }}
          >
            Raw transcripts are visible only here, to signed-in readers, and are never emailed.
          </p>

          {/* Paper leaves the system, so it carries its own handling notice — on
              screen this context is the surrounding app, in a filed copy there is
              nothing but the page. */}
          <p className="text-caption print-only" style={{ color: 'var(--color-text-tertiary)' }}>
            Written up from a Capston Experience call. This page may hold personal information —
            store and share it accordingly. The verbatim transcript is deliberately not included: it
            stays in the system, readable only by signed-in readers.
          </p>
        </div>
      )}
    </main>
  )
}

function Timestamp({ label, value }) {
  return (
    <div>
      <dt
        className="text-caption"
        style={{
          color: 'var(--color-text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}
      >
        {label}
      </dt>
      <dd className="text-body-sm">{value ? formatDateTime(value) : '—'}</dd>
    </div>
  )
}

function PrintButton() {
  // Prints what is on screen — the write-up, never the transcript, which is
  // marked .no-print. "Save as PDF" is the browser's own print destination, so
  // filing a copy needs no export route and no second renderer that could drift
  // from this page.
  return (
    <button
      type="button"
      className="btn btn--secondary btn--sm no-print"
      onClick={() => window.print()}
    >
      Print / save as PDF
    </button>
  )
}

function CallRatingControl({ base, call, onRated }) {
  // The verdict on THIS call. Clicking the thumb that's already set clears it —
  // the same endpoint sets, changes, and unsets, so there is no separate "remove
  // rating" affordance to find.
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState(null)

  async function rate(direction) {
    const next = call.rating === direction ? null : direction
    setBusy(direction)
    setError(null)
    try {
      const out = await api(`${base}/calls/${call.attempt}/rating`, {
        method: 'PUT',
        body: { rating: next },
      })
      onRated(call.attempt, out)
    } catch (err) {
      setError(err.detail || 'Could not save your rating.')
    } finally {
      setBusy(null)
    }
  }

  return (
    // md:ml-auto, not a plain auto margin: at 375px the label plus two 44px
    // targets is wider than the card, so on a phone this sits on its own line
    // under the chips instead of overflowing them.
    <div className="flex flex-col md:ml-auto" style={{ gap: 'var(--space-2xs)' }}>
      <div className="flex items-center flex-wrap" style={{ gap: 'var(--space-xs)' }}>
        <span className="text-body-sm" style={{ color: 'var(--color-text-secondary)' }}>
          {call.rated_at ? `Rated ${formatDateTime(call.rated_at)}` : 'Rate this call'}
        </span>
        {['up', 'down'].map((direction) => (
          <button
            key={direction}
            type="button"
            className={`rating-btn rating-btn--${direction}`}
            aria-pressed={call.rating === direction}
            aria-label={direction === 'up' ? 'Rate this call thumbs up' : 'Rate this call thumbs down'}
            title={direction === 'up' ? 'Thumbs up' : 'Thumbs down'}
            disabled={busy !== null}
            onClick={() => rate(direction)}
          >
            <ThumbIcon direction={direction} size={20} filled={call.rating === direction} />
          </button>
        ))}
      </div>
      {error && (
        <p
          className="text-caption"
          role="alert"
          style={{ color: 'var(--color-text-error)', margin: 0 }}
        >
          {error}
        </p>
      )}
    </div>
  )
}

function CallPanel({ call, base, onRated }) {
  const chips = (
    // items-center, not items-start: this row mixes a 24px chip, a 20px line of
    // text, a small button and two 44px touch targets, so top-aligning them reads
    // as four different heights instead of one toolbar.
    <div
      className="flex items-center flex-wrap"
      style={{ gap: 'var(--space-xs)', marginBottom: 'var(--space-s)' }}
    >
      {/* Names the call it belongs to: on paper the switcher above is gone, so
          this is the only thing saying which of several calls this is. */}
      <span className="text-body-sm" style={{ fontWeight: 600 }}>
        Call {call.attempt}
      </span>
      {call.ended_at && (
        <span className="text-body-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Ended {formatDateTime(call.ended_at)}
        </span>
      )}
      {call.evaluation && <PrintButton />}
      {/* Ratable whether or not a write-up exists: a call that ended after two
          minutes can be judged without waiting for derivation to describe it. */}
      <CallRatingControl base={base} call={call} onRated={onRated} />
    </div>
  )

  if (!call.evaluation) {
    return (
      <div className="card">
        {chips}
        <div className="alert alert--info text-body-sm" role="status">
          {call.has_transcript
            ? 'This call is still being written up — processing retries automatically. Check back shortly.'
            : 'No transcript arrived for this call, so there is nothing to write up.'}
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      {chips}
      {/* The evaluator's markdown, rendered by the one in-repo renderer. Its
          shape is the template's evaluator prompt's business, not this page's —
          whatever headings and sections it asked for are what appear here. */}
      <Markdown source={call.evaluation} className="markdown--report" />
      {call.evaluated_at && (
        <p
          className="text-caption"
          style={{ color: 'var(--color-text-tertiary)', marginTop: 'var(--space-m)' }}
        >
          Written up {formatDateTime(call.evaluated_at)}
        </p>
      )}
    </div>
  )
}

function fmtCallTime(secs) {
  const m = Math.floor(secs / 60)
  const s = String(Math.floor(secs % 60)).padStart(2, '0')
  return `${m}:${s}`
}

function TranscriptCard({ base, call }) {
  // Fetched on demand from the dedicated per-call endpoint — the result payload
  // itself carries no transcript. The parent keys this component on the attempt,
  // so state resets when the reader switches calls.
  const [payload, setPayload] = useState(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function show() {
    setOpen(true)
    if (payload) return
    setBusy(true)
    setError(null)
    try {
      setPayload(await api(`${base}/calls/${call.attempt}/transcript`))
    } catch (err) {
      setError(err.detail || 'Could not load the transcript.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card no-print">
      <h3 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
        Raw transcript
      </h3>
      <p
        className="text-body-sm"
        style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-s)' }}
      >
        The verbatim, unedited record of this call. Treat it as confidential — it stays in the
        system and is never emailed or exported.
      </p>
      {!open && (
        <button type="button" className="btn btn--secondary btn--sm" onClick={show}>
          View raw transcript
        </button>
      )}
      {open && busy && (
        <p className="text-body-sm" role="status" style={{ color: 'var(--color-text-secondary)' }}>
          Loading transcript…
        </p>
      )}
      {open && !busy && error && (
        <div className="alert alert--error" role="alert">
          <div className="flex flex-col" style={{ gap: 'var(--space-xs)' }}>
            <p className="text-body-sm">{error}</p>
            <div>
              <button type="button" className="btn btn--secondary btn--sm" onClick={show}>
                Try again
              </button>
            </div>
          </div>
        </div>
      )}
      {open && !busy && !error && payload && (
        <>
          <TranscriptTurns turns={payload.transcript} />
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setOpen(false)}
            style={{ marginTop: 'var(--space-s)' }}
          >
            Hide transcript
          </button>
        </>
      )}
    </div>
  )
}

function TranscriptTurns({ turns }) {
  if (!Array.isArray(turns)) {
    // Defensive: the transcript is a verbatim passthrough, so render an
    // unexpected shape as-is rather than a blank screen.
    return (
      <pre className="text-body-sm" style={{ overflowX: 'auto', margin: 0 }}>
        {JSON.stringify(turns, null, 2)}
      </pre>
    )
  }
  const spoken = turns.filter((turn) => turn && turn.message)
  if (spoken.length === 0) {
    return (
      <p className="text-body-sm" style={{ color: 'var(--color-text-tertiary)' }}>
        The call recorded no spoken turns.
      </p>
    )
  }
  return (
    <ol
      className="flex flex-col"
      style={{ gap: 'var(--space-s)', listStyle: 'none', margin: 0, padding: 0 }}
    >
      {spoken.map((turn, index) => {
        // The participant's words are what a reader is here for, so they carry
        // the primary ink; the agent's side recedes to secondary.
        const isAgent = turn.role === 'agent'
        return (
          <li key={index}>
            <p
              className="text-caption"
              style={{
                color: isAgent ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: isAgent ? 400 : 600,
              }}
            >
              {isAgent ? 'Interviewer' : 'Staff member'}
              {Number.isFinite(turn.time_in_call_secs) &&
                ` · ${fmtCallTime(turn.time_in_call_secs)}`}
            </p>
            <p
              className="text-body-md"
              style={{
                marginTop: 'var(--space-2xs)',
                color: isAgent ? 'var(--color-text-secondary)' : 'var(--color-text-primary)',
              }}
            >
              {turn.message}
            </p>
          </li>
        )
      })}
    </ol>
  )
}
